import React, { useState, useEffect } from 'react';
import { getConsultantReviewsApi, updateReviewByAdminApi, toggleReviewStatusApi, deleteReviewApi } from '../../services/consultantManagement';
import { toastMessage } from '../../utils/toastMessage';
import { dateFormat } from '../../utils/dateFormat';
import moment from 'moment-timezone';
import useFullPageLoader from '../../hooks/useFullPageLoader';
import useButtonLoader from '../../hooks/UseButtonLoader';
import ResponsivePagination from '../../components/ResponsivePagination';
import ModalBox from '../../components/modal/ModalBox';
import { Input, TextArea } from '../../components/Form';
import profile from '../../Assests/image/profile_not_found.png';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import Switch from '../../components/Switch/Switch';

const ConsultantReviewsListing = ({ consultantId, search, startDate, endDate }) => {
  const [reviewsList, setReviewsList] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  
  // Edit review states
  const [editModal, setEditModal] = useState(false);
  const [editReviewData, setEditReviewData] = useState({
    reviewId: "",
    review: "",
    rating: 5,
    userName: "",
    customDate: "",
    customTime: "",
    ratingType: "chat",
    reply: ""
  });
  const [editUserImage, setEditUserImage] = useState(null);
  const [editUserImagePreview, setEditUserImagePreview] = useState("");
  const [editError, setEditError] = useState({});
  const [editButtonref, editButtonLoader] = useButtonLoader("Update Review");
  
  // View review states
  const [viewModal, setViewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const fetchReviewsList = async (page = 1) => {
    try {
      showLoader();
      const sendData = {
        page: page,
        limit: 10,
        consultantId: consultantId
      };
      if (search) {
        sendData.search = search;
      }
      if (startDate) {
        sendData.startDate = startDate;
      }
      if (endDate) {
        sendData.endDate = endDate;
      }
      
      const { data } = await getConsultantReviewsApi(sendData);
      
      if (data.code === 200) {
        setReviewsList(data?.result?.list || []);
        setTotal(data?.result?.total || 0);
        setActivePage(page);
      } else {
        setReviewsList([]);
        setTotal(0);
        toastMessage(data.message, "error", "reviewsList");
      }
    } catch (error) {
      console.log("error", error);
      setReviewsList([]);
      setTotal(0);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (consultantId) {
      fetchReviewsList(1);
    }
  }, [consultantId, search, startDate, endDate]);

  const handlePageChange = (page) => {
    fetchReviewsList(page);
  };

  const formatDateTime = (review) => {
    // Use customDate if available, otherwise use createdAt
    const dateToUse = review.customDate || review.displayDate || review.createdAt;
    
    // If customTime is available, format date without time and append customTime with AM/PM
    if (review.customTime) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const dateOnly = moment(dateToUse).tz(timezone).format("DD MMM YYYY");
      
      // Format customTime to include AM/PM
      // customTime might be in HH:mm or hh:mm format, convert it to hh:mm A
      let formattedTime = review.customTime;
      if (review.customTime && !review.customTime.includes('AM') && !review.customTime.includes('PM')) {
        // Parse time and convert to 12-hour format with AM/PM
        const timeParts = review.customTime.split(':');
        if (timeParts.length === 2) {
          let hours = parseInt(timeParts[0], 10);
          const minutes = timeParts[1];
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12;
          hours = hours ? hours : 12; // the hour '0' should be '12'
          formattedTime = `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        }
      }
      
      return `${dateOnly}, ${formattedTime}`;
    }
    
    // Otherwise, use dateFormat which includes time from the date
    return dateFormat(dateToUse);
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          style={{
            color: i <= rating ? '#FFD700' : '#ddd',
            fontSize: '18px'
          }}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  // Edit review handlers
  const handleEditClick = (review) => {
    // Format date for input (YYYY-MM-DD)
    let dateStr = "";
    if (review.customDate) {
      const date = new Date(review.customDate);
      dateStr = date.toISOString().split('T')[0];
    } else if (review.displayDate) {
      const date = new Date(review.displayDate);
      dateStr = date.toISOString().split('T')[0];
    } else if (review.createdAt) {
      const date = new Date(review.createdAt);
      dateStr = date.toISOString().split('T')[0];
    }

    setEditReviewData({
      reviewId: review._id,
      review: review.review || "",
      rating: review.rating || 5,
      userName: review.displayName || review.userName || "",
      customDate: dateStr,
      customTime: review.customTime || "",
      ratingType: review.ratingType || "chat",
      reply: review.reviewReply?.reply || ""
    });
    
    // Set image preview if available
    if (review.displayImage) {
      setEditUserImagePreview(review.displayImage);
    } else {
      setEditUserImagePreview("");
    }
    setEditUserImage(null);
    setEditError({});
    setEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditReviewData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (editError[name]) {
      setEditError(prev => {
        const newError = { ...prev };
        delete newError[name];
        return newError;
      });
    }
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toastMessage('Please select a valid image file', 'error');
        return;
      }
      
      setEditUserImage(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditUserImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateEditForm = () => {
    let isValid = true;
    const errors = {};

    if (!editReviewData.review || editReviewData.review.trim() === "") {
      errors.review = "Review is required";
      isValid = false;
    }

    if (!editReviewData.rating || editReviewData.rating < 1 || editReviewData.rating > 5) {
      errors.rating = "Rating must be between 1 and 5";
      isValid = false;
    }

    setEditError(errors);
    return isValid;
  };

  // View review handler
  const handleViewReview = (review) => {
    setSelectedReview(review);
    setViewModal(true);
  };

  const handleUpdateReview = async () => {
    if (!validateEditForm()) {
      return;
    }

    try {
      editButtonLoader(true);
      const formDataToSend = new FormData();
      
      formDataToSend.append("reviewId", editReviewData.reviewId);
      formDataToSend.append("review", editReviewData.review);
      formDataToSend.append("rating", editReviewData.rating);
      formDataToSend.append("userName", editReviewData.userName || "");
      formDataToSend.append("customDate", editReviewData.customDate || "");
      formDataToSend.append("customTime", editReviewData.customTime || "");
      formDataToSend.append("ratingType", editReviewData.ratingType || "chat");
      formDataToSend.append("reply", editReviewData.reply || "");
      
      if (editUserImage) {
        formDataToSend.append("userImage", editUserImage);
      }
      
      const res = await updateReviewByAdminApi(formDataToSend);
      
      if (res?.data?.code === 200) {
        toastMessage(
          res?.data?.message || "Review updated successfully", 
          "success", 
          "updateReview"
        );
        setEditModal(false);
        setEditReviewData({
          reviewId: "",
          review: "",
          rating: 5,
          userName: "",
          customDate: "",
          customTime: "",
          ratingType: "chat",
          reply: ""
        });
        setEditUserImage(null);
        setEditUserImagePreview("");
        setEditError({});
        // Refresh the list
        fetchReviewsList(activePage);
      } else {
        toastMessage(
          res?.data?.message || "Failed to update review", 
          "error", 
          "updateReview"
        );
      }
    } catch (e) {
      console.log(e.message);
      toastMessage(
        "An error occurred while updating review", 
        "error", 
        "updateReview"
      );
    } finally {
      editButtonLoader(false);
    }
  };

  // Toggle review status (show/hide) using Switch
  const handleToggleReviewStatus = async (review, newStatus) => {
    try {
      const res = await toggleReviewStatusApi({
        reviewId: review._id,
        status: newStatus
      });
      
      if (res?.data?.code === 200) {
        toastMessage(
          res?.data?.message || (newStatus ? "Review shown successfully" : "Review hidden successfully"), 
          "success", 
          "toggleReview"
        );
        // Refresh the list
        fetchReviewsList(activePage);
      } else {
        toastMessage(
          res?.data?.message || "Failed to toggle review status", 
          "error", 
          "toggleReview"
        );
        // Refresh to revert the switch state
        fetchReviewsList(activePage);
      }
    } catch (e) {
      console.log(e.message);
      toastMessage(
        "An error occurred while toggling review status", 
        "error", 
        "toggleReview"
      );
      // Refresh to revert the switch state
      fetchReviewsList(activePage);
    }
  };

  // Delete review permanently
  const handleDeleteReview = async (review) => {
    if (!window.confirm(`Are you sure you want to delete this review? This action cannot be undone.`)) {
      return;
    }

    try {
      const res = await deleteReviewApi({
        reviewId: review._id
      });
      
      if (res?.data?.code === 200) {
        toastMessage(
          res?.data?.message || "Review deleted successfully", 
          "success", 
          "deleteReview"
        );
        // Refresh the list
        fetchReviewsList(activePage);
      } else {
        toastMessage(
          res?.data?.message || "Failed to delete review", 
          "error", 
          "deleteReview"
        );
      }
    } catch (e) {
      console.log(e.message);
      toastMessage(
        "An error occurred while deleting review", 
        "error", 
        "deleteReview"
      );
    }
  };

  return (
    <>
      {loader}
      <div className="content-table">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Rating</th>
              <th>Date & Time</th>
              <th>Type</th>
              <th>Reply</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {reviewsList.length > 0 ? (
              reviewsList.map((review, index) => (
                <tr key={index}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img
                        src={review.displayImage || profile}
                        alt={review.displayName || "User"}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.src = profile;
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: '500' }}>
                          {review.displayName || "---"}
                        </div>
                        {review.isAdminCreated && (
                          <small style={{ color: '#1976d2', fontSize: '11px' }}>
                            Admin Created
                          </small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {renderStars(review.rating || 0)}
                      <span style={{ marginLeft: '5px', fontWeight: '500' }}>
                        ({review.rating || 0})
                      </span>
                    </div>
                  </td>
               
                  <td>
                    {formatDateTime(review)}
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: review.ratingType === 'call' ? '#e3f2fd' : '#f3e5f5',
                      color: review.ratingType === 'call' ? '#1976d2' : '#7b1fa2',
                      fontSize: '12px',
                      textTransform: 'capitalize'
                    }}>
                      {review.ratingType || 'chat'}
                    </span>
                  </td>
                  <td style={{ maxWidth: '250px', wordWrap: 'break-word' }}>
                    {review.reviewReply?.reply ? (
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {review.reviewReply.consultantDetails?.name || "Consultant"}:
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          Replied
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>No reply</span>
                    )}
                  </td>
                 
                  <td>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <IconButton
                        aria-label="view"
                        onClick={() => handleViewReview(review)}
                        title="View Review"
                      >
                        <VisibilityIcon style={{ fontSize: '20px', color: '#28a745' }} />
                      </IconButton>
                      <IconButton
                        aria-label="edit"
                        onClick={() => handleEditClick(review)}
                        title="Edit Review"
                      >
                        <EditIcon style={{ fontSize: '20px', color: '#1976d2' }} />
                      </IconButton>
                        <Switch
                          checked={review.status}
                          onChange={() => handleToggleReviewStatus(review, !review.status)}
                        />
                        
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleDeleteReview(review)}
                        title="Delete Review"
                      >
                        <DeleteIcon style={{ fontSize: '20px', color: '#f44336' }} />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center">
                  <div className="py-4">
                    <p className="text-muted">No reviews found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {total > 0 && (
          <div className="mt-3">
            <ResponsivePagination
              currentPage={activePage}
              totalPages={Math.ceil(total / 10)}
              onPageChange={handlePageChange}
              totalItems={total}
              itemsPerPage={10}
            />
          </div>
        )}
      </div>

      {/* Edit Review Modal */}
      <ModalBox
        status={editModal}
        onClose={() => {
          setEditModal(false);
          setEditReviewData({
            reviewId: "",
            review: "",
            rating: 5,
            userName: "",
            customDate: "",
            customTime: "",
            ratingType: "chat",
            reply: ""
          });
          setEditUserImage(null);
          setEditUserImagePreview("");
          setEditError({});
        }}
        title="Edit Review"
        closeIcon
        size={"lg"}
        custom
        customizedFooter={
          <div className="model_custom_footer">
            <button
              className="btn_size mx-4"
              ref={editButtonref}
              onClick={handleUpdateReview}
              style={{ backgroundColor: '#1976d2', color: 'white' }}
            >
              Update Review
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Review Text */}
          <div className="form-group">
            <TextArea
              name="review"
              value={editReviewData.review}
              onChange={handleEditInputChange}
              required
              placeholder="Enter review text"
              className={`form-control ${editError.review ? 'is-invalid' : ''}`}
              label={"Review *"}
              error={editError.review}
              rows={4}
            />
          </div>

          {/* Rating */}
          <div className="form-group">
            <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
              Rating * (1-5)
            </label>
            <Input
              type="number"
              name="rating"
              value={editReviewData.rating}
              onChange={handleEditInputChange}
              required
              min="1"
              max="5"
              placeholder="Rating"
              className={`form-control ${editError.rating ? 'is-invalid' : ''}`}
              error={editError.rating}
            />
          </div>

          {/* User Name */}
          <div className="form-group">
            <Input
              type="text"
              name="userName"
              value={editReviewData.userName}
              onChange={handleEditInputChange}
              placeholder="User Name"
              className="form-control"
              label={"User Name"}
              maxLength={50}
            />
          </div>

          {/* Date and Time Row */}
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: '200px' }}>
              <Input
                type="date"
                name="customDate"
                value={editReviewData.customDate}
                onChange={handleEditInputChange}
                placeholder="Date"
                className="form-control"
                label={"Date (Optional)"}
              />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: '200px' }}>
              <Input
                type="time"
                name="customTime"
                value={editReviewData.customTime}
                onChange={handleEditInputChange}
                placeholder="Time"
                className="form-control"
                label={"Time (Optional)"}
              />
            </div>
          </div>

          {/* Rating Type */}
          <div className="form-group">
            <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
              Rating Type
            </label>
            <select
              name="ratingType"
              value={editReviewData.ratingType}
              onChange={handleEditInputChange}
              className="form-control"
            >
              <option value="chat">Chat</option>
              <option value="call">Call</option>
            </select>
          </div>

          {/* User Image */}
          <div className="form-group">
            <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
              User Image (Optional)
            </label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleEditImageChange}
              className="form-control"
            />
            {editUserImagePreview && (
              <img 
                src={editUserImagePreview} 
                alt="User Preview" 
                style={{ 
                  maxWidth: '150px', 
                  height: 'auto', 
                  borderRadius: 6, 
                  border: '1px solid #eee',
                  marginTop: 8
                }}
              />
            )}
          </div>

          {/* Consultant Reply */}
          <div className="form-group">
            <TextArea
              name="reply"
              value={editReviewData.reply}
              onChange={handleEditInputChange}
              placeholder="Consultant Reply (Optional)"
              className="form-control"
              label={"Consultant Reply (Optional)"}
              rows={3}
            />
            <small className="text-muted">
              Leave empty to remove existing reply, or update with new reply.
            </small>
          </div>
        </div>
      </ModalBox>

      {/* View Review Modal */}
      <ModalBox
        status={viewModal}
        onClose={() => {
          setViewModal(false);
          setSelectedReview(null);
        }}
        title="Review Details"
        closeIcon
        size={"lg"}
        custom
      >
        {selectedReview && (
          <div style={{ padding: '20px' }}>
            {/* User Info Section */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '15px', 
              marginBottom: '20px',
              paddingBottom: '20px',
              borderBottom: '1px solid #eee'
            }}>
              <img
                src={selectedReview.displayImage || profile}
                alt={selectedReview.displayName || "User"}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #eee'
                }}
                onError={(e) => {
                  e.target.src = profile;
                }}
              />
              <div style={{ flex: 1 }}>
                <h5 style={{ margin: 0, marginBottom: '5px', color: '#333' }}>
                  {selectedReview.displayName || "---"}
                </h5>
                {selectedReview.isAdminCreated && (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    Admin Created
                  </span>
                )}
                {!selectedReview.isAdminCreated && (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    User Review
                  </span>
                )}
              </div>
            </div>

            {/* Rating Section */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: '#666', 
                marginBottom: '10px',
                display: 'block'
              }}>
                Rating
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {renderStars(selectedReview.rating || 0)}
                <span style={{ 
                  fontSize: '18px', 
                  fontWeight: '600',
                  color: '#333'
                }}>
                  ({selectedReview.rating || 0} / 5)
                </span>
                <span style={{
                  marginLeft: '15px',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  backgroundColor: selectedReview.ratingType === 'call' ? '#e3f2fd' : '#f3e5f5',
                  color: selectedReview.ratingType === 'call' ? '#1976d2' : '#7b1fa2',
                  fontSize: '12px',
                  textTransform: 'capitalize',
                  fontWeight: '500'
                }}>
                  {selectedReview.ratingType || 'chat'}
                </span>
              </div>
            </div>

            {/* Review Text Section */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: '#666', 
                marginBottom: '10px',
                display: 'block'
              }}>
                Review
              </label>
              <div style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                lineHeight: '1.6',
                color: '#333',
                minHeight: '60px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}>
                {selectedReview.review || "---"}
              </div>
            </div>

            {/* Date & Time Section */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: '#666', 
                marginBottom: '10px',
                display: 'block'
              }}>
                Date & Time
              </label>
              <div style={{
                padding: '10px 15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                color: '#666',
                fontSize: '14px'
              }}>
                {formatDateTime(selectedReview)}
              </div>
            </div>

            {/* Consultant Reply Section */}
            {selectedReview.reviewReply?.reply && (
              <div style={{ 
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid #eee'
              }}>
                <label style={{ 
                  fontWeight: 600, 
                  color: '#666', 
                  marginBottom: '10px',
                  display: 'block'
                }}>
                  Consultant Reply
                </label>
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start'
                }}>
                  {selectedReview.reviewReply.consultantDetails?.profileImage && (
                    <img
                      src={selectedReview.reviewReply.consultantDetails.profileImage}
                      alt="Consultant"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '5px'
                    }}>
                      {selectedReview.reviewReply.consultantDetails?.name || "Consultant"}
                    </div>
                    <div style={{
                      padding: '12px 15px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: '8px',
                      border: '1px solid #bbdefb',
                      lineHeight: '1.6',
                      color: '#333',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}>
                      {selectedReview.reviewReply.reply}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!selectedReview.reviewReply?.reply && (
              <div style={{ 
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid #eee',
                textAlign: 'center',
                color: '#999',
                fontStyle: 'italic'
              }}>
                No reply from consultant
              </div>
            )}
          </div>
        )}
      </ModalBox>
    </>
  );
};

export default ConsultantReviewsListing;

